import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Loader2, Clock, MessageSquare, Inbox, Send } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge, CategoryBadge, PriorityBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState(null);
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/concerns").then((r) => setConcerns(r.data)).catch(() => setConcerns([]));
  useEffect(() => { load(); }, []);

  const open = async (c) => {
    setActive({ ...c, timeline: null });
    try {
      const { data } = await api.get(`/concerns/${c.id}`);
      setActive(data);
    } catch {
      toast.error("Could not load details.");
    }
  };

  const addComment = async () => {
    if (!note.trim()) return;
    setSending(true);
    try {
      await api.post(`/concerns/${active.id}/notes`, { content: note, internal: false });
      const { data } = await api.get(`/concerns/${active.id}`);
      setActive(data);
      setNote("");
      toast.success("Comment added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add comment.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Layout showFooter={false}>
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm text-charcoal-600">Welcome back,</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-forest-900">{user?.name || "Friend"}</h1>
            <p className="mt-2 text-charcoal-600">Track the concerns you've shared and their progress.</p>
          </div>
          <Button onClick={() => navigate("/report")} data-testid="employee-new-concern-btn" className="rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white px-6">
            <Plus className="h-4 w-4 mr-1.5" strokeWidth={2} /> Share a Concern
          </Button>
        </div>

        <div className="mt-10">
          {concerns === null ? (
            <div className="py-20 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest-600" /></div>
          ) : concerns.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border border-forest-600/10">
              <span className="grid place-items-center h-14 w-14 rounded-full bg-forest-50 text-forest-600 mx-auto"><Inbox className="h-7 w-7" strokeWidth={1.5} /></span>
              <h3 className="mt-4 font-serif text-xl text-forest-900">No concerns yet</h3>
              <p className="mt-1.5 text-charcoal-600">When you share a concern, it will appear here.</p>
              <Button onClick={() => navigate("/report")} className="mt-5 rounded-full bg-forest-600 hover:bg-forest-700 text-bone px-6">Share your first concern</Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {concerns.map((c, i) => (
                <motion.button
                  key={c.id}
                  onClick={() => open(c)}
                  data-testid={`employee-concern-${c.case_number}`}
                  initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  className="text-left bg-white rounded-2xl border border-forest-600/10 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-semibold text-charcoal-600">{c.case_number}</span>
                      <CategoryBadge category={c.category} />
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                  <h3 className="mt-2.5 font-serif text-xl text-forest-900">{c.title}</h3>
                  <p className="mt-1 text-sm text-charcoal-600 line-clamp-2">{c.description}</p>
                  <p className="mt-3 text-xs text-charcoal-600">Updated {fmt(c.updated_at)}</p>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl bg-bone rounded-2xl max-h-[88vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-charcoal-600">{active.case_number}</span>
                  <CategoryBadge category={active.category} />
                  <StatusBadge status={active.status} />
                </div>
                <DialogTitle className="font-serif text-2xl text-forest-900 text-left mt-2">{active.title}</DialogTitle>
              </DialogHeader>

              <p className="text-charcoal-700 leading-relaxed">{active.description}</p>
              {active.assigned_to_name && <p className="text-sm text-charcoal-600">Specialist: <span className="font-medium text-forest-800">{active.assigned_to_name}</span></p>}

              <div className="mt-2">
                <h4 className="font-serif text-lg text-forest-900 mb-3">Activity</h4>
                {active.timeline === null ? (
                  <Loader2 className="h-5 w-5 animate-spin text-forest-600" />
                ) : (
                  <ol className="space-y-3">
                    {active.timeline.map((ev) => (
                      <li key={ev.id} className="flex gap-3">
                        <span className="grid place-items-center h-8 w-8 rounded-full bg-forest-50 text-forest-600 shrink-0">
                          {ev.type === "note" ? <MessageSquare className="h-4 w-4" strokeWidth={1.5} /> : <Clock className="h-4 w-4" strokeWidth={1.5} />}
                        </span>
                        <div>
                          <p className="text-sm text-charcoal-700">{ev.content}</p>
                          <p className="text-xs text-charcoal-600 mt-0.5">{ev.author_name} • {fmt(ev.created_at)}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              <div className="mt-2 border-t border-forest-600/10 pt-4">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="employee-comment-input"
                  placeholder="Add a comment or additional detail…" rows={2}
                  className="bg-white rounded-lg border-forest-600/15 resize-none" />
                <Button onClick={addComment} disabled={sending || !note.trim()} data-testid="employee-comment-submit"
                  className="mt-2 rounded-full bg-forest-600 hover:bg-forest-700 text-bone">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add comment</>}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
